import type { CollectionPageResponse, CollectionSort, FetchCollectionPageParams } from 'framework/ViewTypes/hooks';
import { businessFunctionMockData } from './mocks/businessFunctionMockData';
import type { BuisnessFunctionFilters, BuisnessFunctionListItem, BuisnessFunctionState } from './types';

const getNormalizedSearchValue = (value: string) => value.trim().toLowerCase();

const sortRows = (rows: BuisnessFunctionListItem[], sort?: CollectionSort) => {
  const entries = Object.entries(sort ?? {});
  if (!entries.length) return rows;

  const [[column, direction]] = entries;
  const multiplier = String(direction).toLowerCase() === 'desc' ? -1 : 1;

  return [...rows].sort((left, right) => {
    const leftValue = left[column as keyof BuisnessFunctionListItem] ?? '';
    const rightValue = right[column as keyof BuisnessFunctionListItem] ?? '';

    return String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: 'base' }) * multiplier;
  });
};

const useBuisnessFunctionService = () => {
  const search = async ({
    pageNumber,
    pageSize,
    filters,
    searchValue,
    sort,
  }: FetchCollectionPageParams<BuisnessFunctionFilters>): Promise<CollectionPageResponse<BuisnessFunctionListItem>> => {
    const normalizedSearchValue = getNormalizedSearchValue(searchValue);

    const filterRows = (rows: BuisnessFunctionListItem[]): BuisnessFunctionListItem[] =>
      rows.flatMap(row => {
        const matchesSearch =
          !normalizedSearchValue ||
          [row.name, row.administrator, row.state].some(value => value.toLowerCase().includes(normalizedSearchValue));
        const selectedStates = filters?.state ?? [];
        const matchesState = !selectedStates.length || selectedStates.includes(row.state as BuisnessFunctionState);
        const matchesAdministrator = !filters?.administrator || row.administrator === filters.administrator;

        if (matchesSearch && matchesState && matchesAdministrator) return [row];

        const children = filterRows(row.children ?? []);
        return children.length ? [{ ...row, children }] : [];
      });

    const filteredRows = filterRows(businessFunctionMockData);

    const sortedRows = sortRows(filteredRows, sort);
    const totalElements = sortedRows.length;
    const totalPages = totalElements === 0 ? 0 : Math.ceil(totalElements / pageSize);

    const safePageNumber = Math.max(0, Math.min(pageNumber, totalPages - 1));
    const startIndex = safePageNumber * pageSize;

    const result = sortedRows.slice(startIndex, startIndex + pageSize);

    return {
      result,
      pageNumber: safePageNumber,
      totalPages,
      totalElements,
    };
  };

  return { search };
};

export default useBuisnessFunctionService;
